import React, { FC, useCallback, useMemo, useState } from 'react';
import { GET_LATEST_RELEASES } from '../NewsAndUpdates';
import { useQuery } from '@apollo/client';
import { GetLatestReleases, GetLatestReleasesVariables } from '../NewsAndUpdates/__queries__';
import { useButton, useI18n, useLogger, useSelector } from '~Hooks';
import Modal from '~ComponentsImproved/Modal';
import ModalWrapper from '~ComponentsImproved/Modal/ModalWrapper';
import styled from '@emotion/styled';
import { BlueButton, Box, TransparentButton } from '~Styles';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { RELEASE_TYPE } from '../../../../../__queries__';
import ReleasesIcon from '~Assets/icons/releases.svg';
import { useHistory } from 'react-router-dom';
import { DateTime } from 'luxon';

const DISMISSED_NOTES_KEY = 'dismissedCompanyNoteIds';
const MAX_DISMISSED_IDS = 20;

const NotesList = styled.ul`
  list-style-type: disc;
  margin: 0;
  padding-left: 20px;
  color: var(--colorWhite);
`;

const NoteItem = styled.li`
  margin-bottom: 4px;
`;

const AllReleasesButton = styled(TransparentButton)`
  text-transform: none;
  min-width: 120px;
`;

const ContinueButton = styled(BlueButton)`
  min-width: 120px;
`;

/** Popup that appears showing a release note if the company is selected in the note */
const NotifyReleaseModal: FC = () => {
  const { i18n } = useI18n();
  const logger = useLogger('notify-release-modal');

  const history = useHistory();
  const isHandy = window.innerWidth <= 600;
  const venueId = useSelector((state) => state.venueId) as string;

  const [dismissedIds, setDismissedIds] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(DISMISSED_NOTES_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const companyId = useSelector((state) => state.company);

  const releasesQuery = useQuery<GetLatestReleases, GetLatestReleasesVariables>(
    GET_LATEST_RELEASES,
    {
      variables: { limit: 25 },
      fetchPolicy: 'cache-and-network',
    },
  );

  const releases = releasesQuery.data?.getLatestReleases?.items || [];

  // Only show release notes from the last 7 days
  const sevenDaysAgo = useMemo(() => DateTime.now().minus({ days: 7 }), []);

  // Find the first release that has at least one undismissed note targeting this company
  const releaseForCompany = releases.find(
    (release) =>
      release.type === RELEASE_TYPE.RELEASE_NOTES &&
      DateTime.fromISO(release.createdAt, { zone: 'utc' }) >= sevenDaysAgo &&
      release.notes.some(
        (note) =>
          note.companyIds &&
          note.companyIds.includes(companyId) &&
          !dismissedIds.includes(note.titleLang.en),
      ),
  );

  // Get all undismissed notes from that release matching this company
  const latestNotesForCompany = releaseForCompany?.notes.filter(
    (note) =>
      note.companyIds &&
      note.companyIds.includes(companyId) &&
      !dismissedIds.includes(note.titleLang.en),
  );

  // Dismiss all currently visible notes and persist to localStorage
  const dismissNotes = useCallback(() => {
    if (!latestNotesForCompany?.length) return;

    const noteIds = latestNotesForCompany.map((note) => note.titleLang.en);
    const updated = [...dismissedIds, ...noteIds].slice(-MAX_DISMISSED_IDS);
    setDismissedIds(updated);
    localStorage.setItem(DISMISSED_NOTES_KEY, JSON.stringify(updated));
  }, [dismissedIds, latestNotesForCompany]);

  const continueButton = useButton({
    onClick: () => {
      logger.info('Pressed continue on company release notes');
      dismissNotes();
    },
  });

  const homePageButton = useButton({
    onClick: () => {
      dismissNotes();
      logger.info('Navigating to releases page from release note modal');
      history.push(`/venue/${venueId}/gateway`);
    },
  });

  if (!releasesQuery.data || !companyId) {
    return null;
  }

  return (
    <Modal isShowing={Boolean(latestNotesForCompany?.length)} backdropClose={false}>
      <ModalWrapper
        header={{ title: i18n('NewsAndUpdates.WeReleasesAFix'), icon: ReleasesIcon }}
        padding={isHandy ? 'small' : '3xl'}>
        <NotesList>
          {latestNotesForCompany?.map((note, index) => (
            <NoteItem key={i18n(note.titleLang) + index}>
              <Paragraph level={3} fontWeight={600} color="var(--colorWhite)" bottom={0}>
                {i18n(note.titleLang)}
              </Paragraph>
            </NoteItem>
          ))}
        </NotesList>

        <Box
          display="flex"
          flexDirection="row"
          gap="small"
          justifyContent="center"
          alignItems="center">
          {!isHandy && (
            <AllReleasesButton {...homePageButton}>
              {i18n('NewsAndUpdates.ViewAllReleases')}
            </AllReleasesButton>
          )}
          <ContinueButton {...continueButton}>{i18n('App.Continue')}</ContinueButton>
        </Box>
      </ModalWrapper>
    </Modal>
  );
};

export default NotifyReleaseModal;
